import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthState } from '../hooks/useAuthState';
import { createEvent, getEvent, createRSVP } from '../lib/firestore/events';
import { getGroup, getGroupMembers } from '../lib/firestore/groups';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { storage } from '../firebase/config';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { generateInviteToken, generateInviteURL } from '../lib/invites';
import { createEventInviteNotification } from '../lib/notifications';
import { sendEventInviteSMS } from '../lib/sms';
import { addEventCohost } from '../lib/firestore/eventCohosts';
import '../styles/CreateEvent.css';

const CreateEvent = () => {
  const [eventName, setEventName] = useState('');
  const [eventDateTime, setEventDateTime] = useState('');
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');
  const [capacity, setCapacity] = useState('');
  const [locationInput, setLocationInput] = useState(null); // For Google Places autocomplete
  const [eventImage, setEventImage] = useState(null);
  const [eventImageUrl, setEventImageUrl] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  // Hosted By & Cohosts
  const [showAddCohosts, setShowAddCohosts] = useState(false);
  const [showCohostModal, setShowCohostModal] = useState(false);
  const [invitedCohosts, setInvitedCohosts] = useState([]);
  const [userData, setUserData] = useState(null);
  
  // Event Rules
  const [allowSubevents, setAllowSubevents] = useState(false);
  const [subeventInviteRule, setSubeventInviteRule] = useState('all_attendees'); // all_attendees, mutuals_only, friends_only
  const [unlimitedSpots, setUnlimitedSpots] = useState(true);
  const [costPerPerson, setCostPerPerson] = useState(0);
  const [website, setWebsite] = useState('');
  const [bringAGuest, setBringAGuest] = useState(0);
  const [guestsCanInvite, setGuestsCanInvite] = useState(false);
  const [guestsCanInviteUnlimited, setGuestsCanInviteUnlimited] = useState(true);
  const [guestsCanInviteLimit, setGuestsCanInviteLimit] = useState(0);
  const [membersCanSponsor, setMembersCanSponsor] = useState(false);
  const [maxSponsorsPerMember, setMaxSponsorsPerMember] = useState(undefined);
  const [sponsorRequireOneEvent, setSponsorRequireOneEvent] = useState(false);
  const [sponsorAccountabilityEnabled, setSponsorAccountabilityEnabled] = useState(false);
  const [sponsorAccountabilityText, setSponsorAccountabilityText] = useState('You are accountable for any member you invite. If a member breaks the rules you will be suspended from the event');
  const [showAccountabilityModal, setShowAccountabilityModal] = useState(false);
  const [attendeesVisible, setAttendeesVisible] = useState('off');
  const [eventVisibility, setEventVisibility] = useState('invite_only');
  
  // Message Boards & Chat
  const [enablePreEventBoard, setEnablePreEventBoard] = useState(false);
  const [enableDuringEventBoard, setEnableDuringEventBoard] = useState(false);
  const [enableDecompressionBoard, setEnableDecompressionBoard] = useState(false);
  const [decompressionDuration, setDecompressionDuration] = useState(7);
  const [enableGroupChat, setEnableGroupChat] = useState(false);
  const [enableDecompressionChat, setEnableDecompressionChat] = useState(false);
  const [decompressionChatDuration, setDecompressionChatDuration] = useState(7);
  
  // Mass Invites (for sub-events)
  const [inviteAllGroupMembers, setInviteAllGroupMembers] = useState(false);
  const [inviteParentEventAttendees, setInviteParentEventAttendees] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { user } = useAuthState();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get hostType and hostId from URL params (for creating events in groups)
  const hostType = searchParams.get('hostType') || 'profile';
  const hostIdParam = searchParams.get('hostId');
  const hostId = hostIdParam || user?.uid || '';

  // Get pre-filled title and description from URL params (from event prompts)
  const prefilledTitle = searchParams.get('title');
  const prefilledDescription = searchParams.get('description');

  // Load user data for "Hosted By" section
  useEffect(() => {
    if (user) {
      loadUserData();
    }
  }, [user]);

  const loadUserData = async () => {
    if (!user) return;
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        setUserData(userDoc.data());
      }
    } catch (err) {
      console.error('Error loading user data:', err);
    }
  };

  // Initialize with pre-filled values if they exist (only on mount)
  useEffect(() => {
    if (prefilledTitle) {
      setEventName(prefilledTitle);
    }
    if (prefilledDescription) {
      setDescription(prefilledDescription);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Initialize Google Places Autocomplete (only if API key is provided)
  useEffect(() => {
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    
    console.log('🔵 [CreateEvent] Checking for Google Maps API key...', {
      hasKey: !!apiKey,
      keyLength: apiKey ? apiKey.length : 0,
      keyPreview: apiKey ? `${apiKey.substring(0, 10)}...` : 'none'
    });
    
    // Only try to load Google Maps if API key is provided
    if (!apiKey || apiKey.trim() === '') {
      console.warn('🔴 [CreateEvent] Google Maps API key not found, skipping autocomplete');
      console.warn('🔴 [CreateEvent] Make sure VITE_GOOGLE_MAPS_API_KEY is set in .env file');
      return;
    }

    // Check if Google Maps API is already loaded
    if (window.google && window.google.maps && window.google.maps.places) {
      initializeAutocomplete();
    } else {
      // Load Google Maps API script if not already loaded
      if (!document.querySelector('script[src*="maps.googleapis.com"]')) {
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
        script.async = true;
        script.defer = true;
        script.onload = () => {
          console.log('✅ [CreateEvent] Google Maps API script loaded successfully');
          if (window.google && window.google.maps && window.google.maps.places) {
            console.log('✅ [CreateEvent] Google Maps Places API is available');
            initializeAutocomplete();
          } else {
            console.error('🔴 [CreateEvent] Google Maps API loaded but Places API not available');
          }
        };
        script.onerror = (error) => {
          console.error('🔴 [CreateEvent] Failed to load Google Maps API script:', error);
          console.error('🔴 [CreateEvent] Check that the API key is valid and Places API is enabled');
          // Don't show error to user, just fall back to regular input
        };
        document.head.appendChild(script);
      } else {
        // Script is loading, wait for it
        const checkGoogle = setInterval(() => {
          if (window.google && window.google.maps && window.google.maps.places) {
            clearInterval(checkGoogle);
            initializeAutocomplete();
          }
        }, 100);
        
        // Timeout after 5 seconds
        setTimeout(() => {
          clearInterval(checkGoogle);
        }, 5000);
      }
    }

    function initializeAutocomplete() {
      // Wait a bit for the input to be rendered - increased delay for better reliability
      const maxRetries = 10;
      let retryCount = 0;
      
      const tryInit = () => {
        const input = document.getElementById('location');
        if (input && !locationInput) {
          try {
            const autocomplete = new window.google.maps.places.Autocomplete(input, {
              // Note: 'address' cannot be mixed with other types, so we use 'establishment' and 'geocode'
              // This will show businesses, landmarks, and addresses
              types: ['establishment', 'geocode'],
              fields: ['formatted_address', 'geometry', 'name', 'place_id', 'address_components'],
              componentRestrictions: { country: ['us'] }, // Optional: restrict to US addresses
            });

            autocomplete.addListener('place_changed', () => {
              const place = autocomplete.getPlace();
              if (place.formatted_address) {
                setLocation(place.formatted_address);
              } else if (place.name) {
                setLocation(place.name);
              }
            });

            setLocationInput(autocomplete);
            console.log('✅ [CreateEvent] Google Places Autocomplete initialized successfully');
            console.log('✅ [CreateEvent] Autocomplete is ready - start typing an address');
            
            // Test if autocomplete is working by checking if it has the necessary methods
            if (autocomplete && typeof autocomplete.getPlace === 'function') {
              console.log('✅ [CreateEvent] Autocomplete object is valid and ready');
            } else {
              console.warn('⚠️ [CreateEvent] Autocomplete object may not be fully initialized');
            }
            
            // Improve dropdown positioning on mobile
            const updatePacPosition = () => {
              const pacContainer = document.querySelector('.pac-container');
              if (pacContainer && window.innerWidth <= 767) {
                const inputRect = input.getBoundingClientRect();
                pacContainer.style.position = 'fixed';
                pacContainer.style.top = `${inputRect.bottom + window.scrollY + 4}px`;
                pacContainer.style.left = `${inputRect.left + window.scrollX}px`;
                pacContainer.style.width = `${inputRect.width}px`;
                pacContainer.style.zIndex = '10002';
              }
            };
            
            // Update position when dropdown appears
            const observer = new MutationObserver(updatePacPosition);
            observer.observe(document.body, { childList: true, subtree: true });
            
            // Also update on input focus
            input.addEventListener('focus', updatePacPosition);
            input.addEventListener('input', () => {
              setTimeout(updatePacPosition, 100);
            });
          } catch (error) {
            console.error('🔴 [CreateEvent] Error initializing autocomplete:', error);
            console.error('🔴 [CreateEvent] Error details:', {
              message: error.message,
              name: error.name,
              stack: error.stack
            });
            console.error('🔴 [CreateEvent] If you see "ApiNotActivatedMapError", enable Places API in Google Cloud Console:');
            console.error('🔴 [CreateEvent] 1. Go to https://console.cloud.google.com/');
            console.error('🔴 [CreateEvent] 2. Select your project');
            console.error('🔴 [CreateEvent] 3. Go to APIs & Services > Library');
            console.error('🔴 [CreateEvent] 4. Search for "Places API"');
            console.error('🔴 [CreateEvent] 5. Click "Enable"');
            if (retryCount < maxRetries) {
              retryCount++;
              console.log(`🔄 [CreateEvent] Retrying autocomplete initialization (${retryCount}/${maxRetries})...`);
              setTimeout(tryInit, 500);
            } else {
              console.error('🔴 [CreateEvent] Failed to initialize autocomplete after all retries');
            }
          }
        } else if (!input && retryCount < maxRetries) {
          retryCount++;
          console.warn(`🔴 [CreateEvent] Location input not found, retrying... (${retryCount}/${maxRetries})`);
          setTimeout(tryInit, 500);
        } else if (retryCount >= maxRetries) {
          console.error('🔴 [CreateEvent] Failed to initialize autocomplete after max retries');
        }
      };
      
      setTimeout(tryInit, 200);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!user) {
      alert('Please log in to upload images');
      return;
    }

    setUploadingImage(true);
    try {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size must be less than 5MB');
        return;
      }

      // Upload to Firebase Storage
      const fileRef = ref(storage, `event-images/${user.uid}/${Date.now()}_${file.name}`);
      await uploadBytes(fileRef, file);
      const downloadURL = await getDownloadURL(fileRef);
      setEventImageUrl(downloadURL);
      setEventImage(file);
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!user) {
      setError('Please log in to create events');
      setLoading(false);
      navigate('/login');
      return;
    }

    try {
      // Parse datetime-local value (format: YYYY-MM-DDTHH:mm)
      const startsAt = eventDateTime ? new Date(eventDateTime) : new Date();

      // Build event data, only including defined values
      const eventData = {
        hostType: hostType === 'group' ? 'group' : hostType === 'event' ? 'event' : 'profile',
        hostId: hostId,
        createdBy: user.uid, // Track who created the event
        title: eventName,
        startsAt: startsAt,
        visibility: eventVisibility,
        rules: {
          attendeesVisible: attendeesVisible,
          membersCanInvite: membersCanSponsor,
          bringAGuest: bringAGuest,
          sponsorshipAtDoor: false,
          allowSubevents: allowSubevents,
          costPerPerson: costPerPerson || 0,
          guestsCanInvite: guestsCanInvite,
          guestsCanInviteUnlimited: guestsCanInviteUnlimited,
        },
        messageBoards: {
          preEvent: enablePreEventBoard,
          duringEvent: enableDuringEventBoard,
          decompression: enableDecompressionBoard
            ? {
                enabled: true,
                durationDays: decompressionDuration,
                opened: false,
              }
            : { enabled: false },
        },
        groupChat: {
          enabled: enableGroupChat,
        },
        decompressionChat: enableDecompressionChat ? {
          enabled: true,
          durationDays: decompressionChatDuration,
        } : { enabled: false },
      };

      // Add subevent invite rule if subevents are allowed
      if (allowSubevents) {
        eventData.rules.subeventInviteRule = subeventInviteRule;
      }

      // Add guests can invite limit if not unlimited
      if (guestsCanInvite && !guestsCanInviteUnlimited) {
        eventData.rules.guestsCanInviteLimit = guestsCanInviteLimit;
      }

      // Only add optional fields if they have truthy values
      if (description && description.trim()) {
        eventData.description = description.trim();
      }
      if (location && location.trim()) {
        eventData.location = {
          text: location.trim(),
        };
      } else {
        eventData.location = {
          text: 'TBD',
        };
      }
      if (eventImageUrl && eventImageUrl.trim()) {
        eventData.image = eventImageUrl.trim();
      }
      if (!unlimitedSpots && capacity && capacity.trim() && !isNaN(parseInt(capacity))) {
        eventData.capacity = parseInt(capacity);
      }
      
      // Normalize website URL - add https:// if missing
      if (website && website.trim()) {
        let normalizedWebsite = website.trim();
        // If it doesn't start with http:// or https://, add https://
        if (!normalizedWebsite.match(/^https?:\/\//i)) {
          normalizedWebsite = `https://${normalizedWebsite}`;
        }
        eventData.website = normalizedWebsite;
      }
      
      // Add sponsorship rules if members can sponsor
      if (membersCanSponsor) {
        if (maxSponsorsPerMember !== undefined && maxSponsorsPerMember !== null) {
          eventData.rules.maxSponsorsPerMember = maxSponsorsPerMember;
        }
        if (sponsorRequireOneEvent) {
          eventData.rules.sponsorEligibility = { minAttended: 1, requireOneEvent: true };
        }
        if (sponsorAccountabilityEnabled && sponsorAccountabilityText && sponsorAccountabilityText.trim()) {
          eventData.rules.sponsorAccountabilityText = sponsorAccountabilityText.trim();
        }
      }
      
      // endsAt is optional, only add if needed in future

      // Clean undefined, null, and empty string values (recursively)
      const cleanEventData = {};
      const removeUndefined = (obj) => {
        const cleaned = {};
        Object.keys(obj).forEach(key => {
          const value = obj[key];
          if (value !== undefined && value !== null && value !== '') {
            if (typeof value === 'object' && !Array.isArray(value) && value !== null && !(value instanceof Date)) {
              const cleanedNested = removeUndefined(value);
              if (Object.keys(cleanedNested).length > 0) {
                cleaned[key] = cleanedNested;
              }
            } else {
              cleaned[key] = value;
            }
          }
        });
        return cleaned;
      };

      const finalEventData = removeUndefined(eventData);
      console.log('🔵 [CreateEvent] Final cleaned eventData:', finalEventData);

      const eventId = await createEvent(finalEventData);

      // Automatically RSVP host as "going"
      try {
        await createRSVP({
          userId: user.uid,
          eventId: eventId,
          state: 'going',
          plusOnes: 0,
        });
        console.log('✅ [CreateEvent] Host auto-RSVPed as going');
      } catch (rsvpError) {
        console.error('⚠️ [CreateEvent] Failed to auto-RSVP host:', rsvpError);
        // Don't fail event creation if RSVP fails
      }

      // Note: Cohosts can be added after event creation from the event settings page
      // This allows the event to exist first, then cohosts can be invited

      // Handle mass invites (run in parallel for better performance)
      if (inviteAllGroupMembers && hostType === 'group') {
        try {
          const members = await getGroupMembers(hostId);
          const inviterName = userData?.firstName && userData?.lastName
            ? `${userData.firstName} ${userData.lastName}`.trim()
            : userData?.firstName || userData?.email || 'Someone';
          
          // Generate all invite tokens in parallel
          const invitePromises = members
            .filter(member => member.userId !== user.uid)
            .map(async (member) => {
              try {
                const inviteToken = await generateInviteToken(
                  { kind: 'event_join', id: eventId },
                  user.uid,
                  { oneTime: false, expiresIn: 7 * 24 * 60 * 60 }
                );
                
                await createEventInviteNotification(
                  member.userId,
                  eventId,
                  eventName || 'Event',
                  inviterName,
                  inviteToken,
                  user.uid
                );

                // Try to send SMS if phone number is available
                try {
                  // Get user's phone number
                  const memberDoc = await getDoc(doc(db, 'users', member.userId));
                  if (memberDoc.exists()) {
                    const memberData = memberDoc.data();
                    const phoneNumber = memberData.phone || memberData.phoneE164;
                    if (phoneNumber) {
                      const inviteURL = generateInviteURL(inviteToken);
                      await sendEventInviteSMS(
                        phoneNumber,
                        inviteURL,
                        eventName || 'Event',
                        inviterName,
                        member.userId, // recipientUserId for consent check
                        user.uid // senderUserId for friends_only check
                      );
                    }
                  }
                } catch (smsError) {
                  // Don't fail the invite if SMS fails (user may have opted out)
                  console.warn(`Failed to send SMS to member ${member.userId}:`, smsError);
                }
              } catch (inviteError) {
                console.error(`Error inviting member ${member.userId}:`, inviteError);
                // Continue with other members
              }
            });
          
          // Wait for all invites to complete (but don't block)
          Promise.all(invitePromises).catch(err => {
            console.error('Some mass invites failed:', err);
          });
        } catch (massInviteError) {
          console.error('Error sending mass invites to group members:', massInviteError);
          // Don't fail event creation if mass invites fail
        }
      }

      if (inviteParentEventAttendees && hostType === 'event') {
        try {
          const parentEvent = await getEvent(hostId);
          if (parentEvent) {
            const rsvpsRef = collection(db, 'events', hostId, 'rsvps');
            const rsvpsSnapshot = await getDocs(rsvpsRef);
            
            const attendees = [];
            rsvpsSnapshot.forEach((doc) => {
              const data = doc.data();
              if (data.state === 'going' || data.state === 'checked_in') {
                attendees.push(doc.id);
              }
            });
            
            const inviterName = userData?.firstName && userData?.lastName
              ? `${userData.firstName} ${userData.lastName}`.trim()
              : userData?.firstName || userData?.email || 'Someone';
            
            // Generate all invite tokens in parallel
            const invitePromises = attendees
              .filter(attendeeId => attendeeId !== user.uid)
              .map(async (attendeeId) => {
                try {
                  const inviteToken = await generateInviteToken(
                    { kind: 'event_join', id: eventId },
                    user.uid,
                    { oneTime: false, expiresIn: 7 * 24 * 60 * 60 }
                  );
                  
                  await createEventInviteNotification(
                    attendeeId,
                    eventId,
                    eventName || 'Event',
                    inviterName,
                    inviteToken,
                    user.uid
                  );

                  // Try to send SMS if phone number is available
                  try {
                    // Get user's phone number
                    const attendeeDoc = await getDoc(doc(db, 'users', attendeeId));
                    if (attendeeDoc.exists()) {
                      const attendeeData = attendeeDoc.data();
                      const phoneNumber = attendeeData.phone || attendeeData.phoneE164;
                      if (phoneNumber) {
                        const inviteURL = generateInviteURL(inviteToken);
                        await sendEventInviteSMS(
                          phoneNumber,
                          inviteURL,
                          eventName || 'Event',
                          inviterName,
                          attendeeId, // recipientUserId for consent check
                          user.uid // senderUserId for friends_only check
                        );
                      }
                    }
                  } catch (smsError) {
                    // Don't fail the invite if SMS fails (user may have opted out)
                    console.warn(`Failed to send SMS to attendee ${attendeeId}:`, smsError);
                  }
                } catch (inviteError) {
                  console.error(`Error inviting attendee ${attendeeId}:`, inviteError);
                  // Continue with other attendees
                }
              });
            
            // Wait for all invites to complete (but don't block)
            Promise.all(invitePromises).catch(err => {
              console.error('Some mass invites failed:', err);
            });
          }
        } catch (massInviteError) {
          console.error('Error sending mass invites to parent event attendees:', massInviteError);
          // Don't fail event creation if mass invites fail
        }
      }

      // Navigate based on host type
      if (hostType === 'group') {
        navigate(`/groups/${hostId}`);
      } else if (hostType === 'event') {
        navigate(`/events/${hostId}`);
      } else {
        navigate('/home');
      }
    } catch (err) {
      setError(err.message || 'Failed to create event. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div 
      className="create-event-container"
      onClick={() => navigate(-1)}
    >
      <div 
        className="create-event-card"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="create-event-header">
          <div className="header-with-image">
            <h1>Create Event</h1>
            <div className="image-upload-section">
              <label htmlFor="eventImage" className="image-upload-label">
                {eventImageUrl ? (
                  <img src={eventImageUrl} alt="Event" className="preview-image" />
                ) : (
                  <div className="image-upload-placeholder">
                    <span>📷</span>
                    <small>Add Photo</small>
                  </div>
                )}
              </label>
              <input
                type="file"
                id="eventImage"
                accept="image/*"
                onChange={handleImageUpload}
                style={{ display: 'none' }}
                disabled={uploadingImage}
              />
              {uploadingImage && <div className="uploading-indicator">Uploading...</div>}
            </div>
          </div>
          <p>Plan your next gathering</p>
        </div>

        <form onSubmit={handleSubmit} className="create-event-form">
          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <label htmlFor="eventName">Event Name *</label>
            <input
              type="text"
              id="eventName"
              className="input"
              placeholder="e.g., Summer BBQ, Birthday Party"
              value={eventName}
              onChange={(e) => setEventName(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              className="input"
              rows="4"
              placeholder="Tell your guests about the event..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          {/* Hosted By Section */}
          <div className="form-section">
            <h3>Hosted By</h3>
            <div className="hosted-by-section">
              <div className="host-creator">
                {userData && (
                  <div className="creator-info">
                    <span className="creator-name">
                      {userData.firstName && userData.lastName
                        ? `${userData.firstName} ${userData.lastName}`.trim()
                        : userData.firstName || userData.email || 'You'}
                    </span>
                    <span className="creator-label">(Creator)</span>
                  </div>
                )}
                {invitedCohosts.length > 0 && (
                  <div className="cohosts-list">
                    {invitedCohosts.map((cohost, idx) => (
                      <div key={idx} className="cohost-item">
                        <span className="cohost-name">{cohost.name}</span>
                        <span className="cohost-label">(Cohost - Pending)</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={showAddCohosts}
                    onChange={(e) => setShowAddCohosts(e.target.checked)}
                  />
                  Add Cohosts
                </label>
                <small className="form-hint">
                  Invite others to help manage this event
                </small>
              </div>
              {showAddCohosts && (
                <div className="form-group">
                  <p className="form-hint" style={{ marginBottom: '0.5rem' }}>
                    Cohost invitations will be sent after the event is created. You can add cohosts later from the event settings.
                  </p>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      // For now, we'll note that cohosts can be added after event creation
                      // In the future, we can add a proper cohost selection modal
                      alert('Cohost invitations will be sent after the event is created. You can also add cohosts later from the event settings page.');
                    }}
                  >
                    Add Cohosts (After Creation)
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="eventDate">Date *</label>
              <input
                type="date"
                id="eventDate"
                className="input input-date"
                value={eventDateTime ? eventDateTime.split('T')[0] : ''}
                onChange={(e) => {
                  const date = e.target.value;
                  const time = eventDateTime ? eventDateTime.split('T')[1] : '12:00';
                  setEventDateTime(date ? `${date}T${time}` : '');
                }}
                min={new Date().toISOString().split('T')[0]}
                required
                onClick={(e) => {
                  // Force Chrome to show native picker
                  e.target.showPicker?.();
                }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="eventTime">Time *</label>
              <input
                type="time"
                id="eventTime"
                className="input input-time"
                value={eventDateTime ? eventDateTime.split('T')[1] : ''}
                onChange={(e) => {
                  const time = e.target.value;
                  const date = eventDateTime ? eventDateTime.split('T')[0] : new Date().toISOString().split('T')[0];
                  setEventDateTime(date ? `${date}T${time}` : '');
                }}
                required
                onClick={(e) => {
                  // Force Chrome to show native picker
                  e.target.showPicker?.();
                }}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="location">Location</label>
            <input
              type="text"
              id="location"
              className="input"
              placeholder="Start typing an address..."
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              autoComplete="off"
            />
            {import.meta.env.VITE_GOOGLE_MAPS_API_KEY ? (
              <small className="form-hint">Address autocomplete will suggest locations as you type (requires Google Maps API key)</small>
            ) : (
              <small className="form-hint">Enter an address or location name. To enable autocomplete, add VITE_GOOGLE_MAPS_API_KEY to your .env file. See GOOGLE_MAPS_SETUP.md for instructions.</small>
            )}
          </div>

          {/* Visibility Section */}
          <div className="form-section" style={{ border: '2px solid var(--primary-color)', backgroundColor: 'var(--background-alt)' }}>
            <h3 style={{ color: 'var(--primary-color)', fontWeight: '700', fontSize: '1.25rem' }}>Visibility & Access</h3>
            <div className="form-group">
              <label style={{ fontWeight: '600', fontSize: '1rem' }}>Event Visibility *</label>
              <div className="select-container" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="eventVisibility"
                    value="invite_only"
                    checked={eventVisibility === 'invite_only'}
                    onChange={(e) => setEventVisibility(e.target.value)}
                  />
                  <span>Invite Only</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="eventVisibility"
                    value="invite_or_apply"
                    checked={eventVisibility === 'invite_or_apply'}
                    onChange={(e) => setEventVisibility(e.target.value)}
                  />
                  <span>Invite or Apply to Join</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="eventVisibility"
                    value="public"
                    checked={eventVisibility === 'public'}
                    onChange={(e) => setEventVisibility(e.target.value)}
                  />
                  <span>Public (Listed)</span>
                </label>
                {hostType === 'group' && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="eventVisibility"
                      value="group_only"
                      checked={eventVisibility === 'group_only'}
                      onChange={(e) => setEventVisibility(e.target.value)}
                    />
                    <span>Visible to Group Members</span>
                  </label>
                )}
                {hostType === 'event' && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="eventVisibility"
                      value="event_only"
                      checked={eventVisibility === 'event_only'}
                      onChange={(e) => setEventVisibility(e.target.value)}
                    />
                    <span>Visible to Event Attendees</span>
                  </label>
                )}
              </div>
              <small className="form-hint">
                {eventVisibility === 'invite_only' && 'Only people with invite links can see and join'}
                {eventVisibility === 'invite_or_apply' && 'People can request to join or be invited'}
                {eventVisibility === 'public' && 'Anyone can discover and join'}
                {eventVisibility === 'group_only' && 'Visible to all members of the parent group. Appears in discover for group members only.'}
                {eventVisibility === 'event_only' && 'Visible to all attendees of the parent event. Appears in discover for event attendees only.'}
              </small>
            </div>
          </div>

          {/* Event Rules Section */}
          <div className="form-section">
            <h3>Event Rules</h3>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={allowSubevents}
                  onChange={(e) => setAllowSubevents(e.target.checked)}
                />
                Allow Subevents
              </label>
              <small className="form-hint">
                Allow this event to have nested sub-events
              </small>
            </div>

            {allowSubevents && (
              <div className="form-group">
                <label htmlFor="subeventInviteRule">Subevent Invite Rules</label>
                <select
                  id="subeventInviteRule"
                  className="input"
                  value={subeventInviteRule}
                  onChange={(e) => setSubeventInviteRule(e.target.value)}
                >
                  <option value="all_attendees">All Attendees</option>
                  <option value="mutuals_only">Mutuals Only</option>
                  <option value="friends_only">Friends Only</option>
                </select>
                <small className="form-hint">
                  Who can sub-events invite from this event
                </small>
              </div>
            )}

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={unlimitedSpots}
                  onChange={(e) => setUnlimitedSpots(e.target.checked)}
                />
                Unlimited Spots
              </label>
            </div>

            {!unlimitedSpots && (
              <div className="form-group">
                <label htmlFor="capacity">Capacity *</label>
                <input
                  type="number"
                  id="capacity"
                  className="input"
                  placeholder="Max number of attendees"
                  min="1"
                  value={capacity}
                  onChange={(e) => setCapacity(e.target.value)}
                  required={!unlimitedSpots}
                />
              </div>
            )}

            <div className="form-group">
              <label htmlFor="costPerPerson">Cost Per Person</label>
              <input
                type="number"
                id="costPerPerson"
                className="input"
                placeholder="0.00"
                min="0"
                step="0.01"
                value={costPerPerson}
                onChange={(e) => setCostPerPerson(parseFloat(e.target.value) || 0)}
              />
              <small className="form-hint">
                Enter 0 for free events
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="website">Website (Optional)</label>
              <input
                type="text"
                id="website"
                className="input"
                placeholder="hotelia.us or www.example.com"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
              />
              <small className="form-hint">
                Link to event website, registration page, or more info (https:// will be added automatically)
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="bringAGuest">How many guests can each attendee bring?</label>
              <input
                type="number"
                id="bringAGuest"
                className="input"
                min="0"
                max="10"
                value={bringAGuest}
                onChange={(e) => setBringAGuest(parseInt(e.target.value) || 0)}
              />
              <small className="form-hint">
                How many guests can each attendee bring with them to the event? Zero means unlimited.
              </small>
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={guestsCanInvite}
                  onChange={(e) => setGuestsCanInvite(e.target.checked)}
                />
                Guests Can Invite Other Guests (event website)
              </label>
            </div>

            {guestsCanInvite && (
              <>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={guestsCanInviteUnlimited}
                      onChange={(e) => setGuestsCanInviteUnlimited(e.target.checked)}
                    />
                    Unlimited Invites
                  </label>
                </div>
                {!guestsCanInviteUnlimited && (
                  <div className="form-group">
                    <label htmlFor="guestsCanInviteLimit">Max Invites Per Guest</label>
                    <input
                      type="number"
                      id="guestsCanInviteLimit"
                      className="input"
                      min="1"
                      max="10"
                      value={guestsCanInviteLimit}
                      onChange={(e) => setGuestsCanInviteLimit(parseInt(e.target.value) || 0)}
                    />
                    <small className="form-hint">
                      Limit how many people each guest can invite (+1, +2, +3, etc.)
                    </small>
                  </div>
                )}
              </>
            )}

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={membersCanSponsor}
                  onChange={(e) => setMembersCanSponsor(e.target.checked)}
                />
                Sponsorship System (Guests sponsor other guests to attend)
              </label>
              <small className="form-hint">
                Allow members to invite/sponsor additional people to this event
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="attendeesVisible">Attendees List Visibility</label>
              <select
                id="attendeesVisible"
                className="input"
                value={attendeesVisible}
                onChange={(e) => setAttendeesVisible(e.target.value)}
              >
                <option value="off">Hidden (Default)</option>
                <option value="visible">Visible</option>
                <option value="mutuals_only">Mutuals Only</option>
                <option value="friends_only">Friends Only</option>
              </select>
              <small className="form-hint">
                Control who can see the attendee list (hidden by default)
              </small>
            </div>
          </div>

          {membersCanSponsor && (
            <div className="form-section">
              <h3>Invite Rules</h3>

              <div className="form-group">
                <label htmlFor="maxSponsors">Max invites per attendee</label>
                <input
                  type="number"
                  id="maxSponsors"
                  className="input"
                  min="0"
                  max="10"
                  placeholder="0 = unlimited"
                  value={maxSponsorsPerMember ?? ''}
                  onChange={(e) => {
                    const value = e.target.value === '' ? undefined : parseInt(e.target.value, 10);
                    const clampedValue = value !== undefined && value > 10 ? 10 : value;
                    setMaxSponsorsPerMember(clampedValue);
                  }}
                />
                <small className="form-hint">
                  Limit how many people each attendee can invite. Zero or leave blank = unlimited.
                </small>
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={sponsorRequireOneEvent}
                    onChange={(e) => setSponsorRequireOneEvent(e.target.checked)}
                  />
                  Members must attend one event before they can invite others
                </label>
                <small className="form-hint">
                  Inviters must have attended at least one event
                </small>
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={sponsorAccountabilityEnabled}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setShowAccountabilityModal(true);
                      } else {
                        setSponsorAccountabilityEnabled(false);
                        setSponsorAccountabilityText('You are accountable for any member you invite. If a member breaks the rules you will be suspended from the event');
                      }
                    }}
                  />
                  Enable Invite Accountability
                </label>
                <small className="form-hint">
                  If invited member violates rules, inviter is also suspended
                </small>
              </div>
            </div>
          )}

          {/* Accountability Rules Modal */}
          {showAccountabilityModal && (
            <div className="modal-overlay" onClick={() => setShowAccountabilityModal(false)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <h2>Invite Accountability Rules</h2>
                <p className="modal-hint">(edit to fit your policies)</p>
                <textarea
                  className="input"
                  rows={6}
                  value={sponsorAccountabilityText}
                  onChange={(e) => setSponsorAccountabilityText(e.target.value)}
                  maxLength={500}
                />
                <div className="modal-actions" style={{ marginTop: '1rem', display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowAccountabilityModal(false);
                      setSponsorAccountabilityText('You are accountable for any member you invite. If a member breaks the rules you will be suspended from the event');
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      setSponsorAccountabilityEnabled(true);
                      setShowAccountabilityModal(false);
                    }}
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="form-section">
            <h3>Message Boards</h3>
            <p className="form-hint">
              Enable message boards for attendees to connect and coordinate
            </p>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={enablePreEventBoard}
                  onChange={(e) => setEnablePreEventBoard(e.target.checked)}
                />
                Enable Pre-Event Message Board
              </label>
              <small className="form-hint">
                Allow attendees to chat before the event (coordinate rides, ask questions, etc.)
              </small>
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={enableDuringEventBoard}
                  onChange={(e) => setEnableDuringEventBoard(e.target.checked)}
                />
                Enable During-Event Message Board
              </label>
              <small className="form-hint">
                Allow attendees to chat during the event
              </small>
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={enableDecompressionBoard}
                  onChange={(e) => setEnableDecompressionBoard(e.target.checked)}
                />
                Enable Post-Event Decompression Board
              </label>
              <small className="form-hint">
                Allow attendees to connect after the event for 3-14 days
              </small>
            </div>

            {enableDecompressionBoard && (
              <div className="form-group">
                <label htmlFor="decompressionDuration">
                  Decompression Board Duration (days)
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[3-9]|1[0-4]"
                  id="decompressionDuration"
                  className="input"
                  min="3"
                  max="14"
                  value={decompressionDuration}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '' || /^\d+$/.test(val)) {
                      const num = parseInt(val) || 0;
                      if (num === 0 || (num >= 3 && num <= 14)) {
                        setDecompressionDuration(num || 7);
                      }
                    }
                  }}
                />
                <small className="form-hint">
                  How long the decompression board stays open (3-14 days)
                </small>
              </div>
            )}

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={enableGroupChat}
                  onChange={(e) => setEnableGroupChat(e.target.checked)}
                />
                Allow Group Chat
              </label>
              <small className="form-hint">
                Enable a group chat that opens 24 hours before the event and closes 24 hours after it ends. Perfect for coordinating with all attendees!
              </small>
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={enableDecompressionChat}
                  onChange={(e) => setEnableDecompressionChat(e.target.checked)}
                />
                Allow Decompression Chat
              </label>
              <small className="form-hint">
                Attendees can chat after the event for a set duration (similar to decompression message board)
              </small>
            </div>

            {enableDecompressionChat && (
              <div className="form-group">
                <label htmlFor="decompressionChatDuration">
                  Decompression Chat Duration (days)
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[3-9]|1[0-4]"
                  id="decompressionChatDuration"
                  className="input"
                  min="3"
                  max="14"
                  value={decompressionChatDuration}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '' || /^\d+$/.test(val)) {
                      const num = parseInt(val) || 0;
                      if (num === 0 || (num >= 3 && num <= 14)) {
                        setDecompressionChatDuration(num || 7);
                      }
                    }
                  }}
                />
                <small className="form-hint">
                  How long the decompression chat stays open (3-14 days)
                </small>
              </div>
            )}
          </div>

          {/* Mass Invite Section (for sub-events) */}
          {(hostType === 'group' || hostType === 'event') && (
            <div className="form-section">
              <h3>Mass Invites</h3>
              <p className="form-hint">
                Automatically invite people when this event is created
              </p>
              
              {hostType === 'group' && (
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={inviteAllGroupMembers}
                      onChange={(e) => setInviteAllGroupMembers(e.target.checked)}
                    />
                    Invite All Group Members
                  </label>
                  <small className="form-hint">
                    Send invites to everyone in the parent group
                  </small>
                </div>
              )}
              
              {hostType === 'event' && (
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={inviteParentEventAttendees}
                      onChange={(e) => setInviteParentEventAttendees(e.target.checked)}
                    />
                    Invite Anyone Who Attended Parent Event
                  </label>
                  <small className="form-hint">
                    Send invites to everyone who attended the parent event
                  </small>
                </div>
              )}
            </div>
          )}

          {hostType === 'group' && (
            <div className="info-banner">
              <p>📁 Creating event in group</p>
            </div>
          )}
          
          {hostType === 'event' && (
            <div className="info-banner">
              <p>🎉 Creating sub-event under parent event</p>
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate(-1)}
              disabled={loading}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Creating...' : 'Create Event'}
            </button>
          </div>
        </form>

      </div>
    </div>
  );
};

export default CreateEvent;


